/**
 * PageHeader Component - Layout
 * Page-level header with title, breadcrumbs, optional help icon, and action buttons
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { HelpButton } from '@/features/help';

interface Breadcrumb {
  label: string;
  path: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
  helpKey?: string;
  onBack?: () => void;
}

export function PageHeader({ title, subtitle, breadcrumbs, actions, helpKey, onBack }: PageHeaderProps) {
  return (
    <div className="mb-6 space-y-2">
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm">
          {breadcrumbs.map((crumb, index) => (
            <span key={crumb.path} className="flex items-center gap-1">
              {index > 0 && (
                <ChevronRight size={14} className="text-muted-foreground" />
              )}
              {index < breadcrumbs.length - 1 ? (
                <Link
                  to={crumb.path}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="font-medium text-foreground">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}

      {/* Title row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Regresar"
              title="Regresar"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:border-primary hover:bg-primary/10 hover:text-primary"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <div>
            <div className="flex items-baseline gap-2">
              <h1 className="text-2xl font-semibold text-foreground leading-none">{title}</h1>
              {helpKey && <span className="relative" style={{ top: '2px' }}><HelpButton pageKey={helpKey} /></span>}
            </div>
            {subtitle && (
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
